/**
 * Deeplink URL Normalization Utility
 *
 * Handles resolution and normalization of all deeplink URL formats:
 * - https://bank.varomoney.com/path
 * - https://uat-bank.varomoney.com/path
 * - https://varomoney.onelink.me/... (AppsFlyer OneLinks)
 * - https://ablink.bank.varomoney.com/... (Braze ABLinks)
 * - acme:/path (custom scheme, single slash)
 * - acme://host/path (custom scheme with host)
 */

import {
  isABLink,
  isAppDeepLink as isAppDeepLinkFromResolver,
  isOnelink,
  resolveManuallyFetchedLink,
} from 'services/deeplinks/helpers/resolveManuallyFetchedLink';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

/**
 * Extract the app path from a URL
 * Handles various URL formats and returns a clean path like "/move-money"
 */
export function extractPathFromUrl(url: string): {
  path: string;
  queryParams: Record<string, string>;
} {
  try {
    const urlObj = new URL(url);

    // For acme: scheme, pathname is the path
    // For https: scheme, pathname is also the path
    let path = urlObj.pathname;

    // Ensure path starts with /
    if (!path.startsWith('/')) {
      path = '/' + path;
    }

    // Parse query params
    const queryParams: Record<string, string> = {};
    urlObj.searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    return { path, queryParams };
  } catch {
    // If URL parsing fails, try to extract path manually
    // This handles malformed URLs gracefully
    logBreadcrumb({
      level: 'warning',
      message: 'URL parsing failed, attempting manual extraction',
      data: { url },
    });

    // Try to find path after known patterns
    const patterns = [
      /acme:\/\/[^/]*(\/.*?)(?:\?|$)/, // acme://host/path
      /acme:(\/.*?)(?:\?|$)/, // acme:/path
      /varo:\/\/[^/]*(\/.*?)(?:\?|$)/, // varo://host/path
      /varo:(\/.*?)(?:\?|$)/, // varo:/path
      /https?:\/\/[^/]+(\/.*?)(?:\?|$)/, // https://host/path
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return { path: match[1], queryParams: {} };
      }
    }

    // Fallback: return root path
    return { path: '/', queryParams: {} };
  }
}

/**
 * Check if a URL should be handled by our app
 * Delegates to the existing isAppDeepLink from resolveManuallyFetchedLink
 *
 * @param url - URL to check
 * @returns True if URL should be handled internally
 */
export function isAppDeepLink(url: string): boolean {
  return isAppDeepLinkFromResolver(url);
}

export interface NormalizedDeeplink {
  /** The original URL before any resolution */
  originalUrl: string;
  /** The resolved URL after ABLink/OneLink resolution */
  resolvedUrl: string;
  /** The extracted app path (e.g., "/move-money") */
  path: string;
  /** Query parameters from the URL */
  queryParams: Record<string, string>;
  /** Whether this URL should be handled by our app */
  isAppLink: boolean;
}

/**
 * Normalize a deeplink URL to an app path
 *
 * This function:
 * 1. Resolves ABLinks and OneLinks to their final destination
 * 2. Extracts the path from the URL
 * 3. Returns a clean path for Expo Router navigation
 *
 * @param url The raw URL to normalize
 * @returns Normalized deeplink info including the path
 */
export async function normalizeDeeplinkUrl(
  url: string,
): Promise<NormalizedDeeplink> {
  logBreadcrumb({
    level: 'info',
    message: 'Normalizing deeplink URL',
    data: { url },
  });

  let resolvedUrl = url;

  // Use the existing resolver for ABLinks and OneLinks
  // This handles HTTP redirect following and HTML parsing
  if (isABLink(url) || isOnelink(url)) {
    try {
      resolvedUrl = await resolveManuallyFetchedLink(url);
    } catch (error) {
      logBreadcrumb({
        level: 'error',
        message: 'Failed to resolve ABLink/OneLink, treating as non-app link',
        data: { url, error },
      });
      // If resolution fails, treat as non-app link
      return {
        originalUrl: url,
        resolvedUrl: url,
        path: '/',
        queryParams: {},
        isAppLink: false,
      };
    }
  }

  // Extract path from resolved URL
  const { path, queryParams } = extractPathFromUrl(resolvedUrl);
  const isAppLink = isAppDeepLink(resolvedUrl);

  logBreadcrumb({
    level: 'info',
    message: 'Deeplink URL normalized',
    data: {
      originalUrl: url,
      resolvedUrl,
      path,
      queryParams,
      isAppLink,
    },
  });

  return {
    originalUrl: url,
    resolvedUrl,
    path,
    queryParams,
    isAppLink,
  };
}
